import type { LearningExercise } from "../types";

// Deutsche Patentanwalts-Ausbildung — Verletzungsrecht
// Abdeckung: § 14 PatG / Art. 69 EPÜ + Auslegungsprotokoll, Äquivalenzlehre (BGH „Schneidmesser"),
//            §§ 9, 10, 11, 12 PatG (Benutzungshandlungen, mittelbare Verletzung, Schranken, Vorbenutzung),
//            Erschöpfung, §§ 139 I+II, 140a, 140b, 140c PatG (Ansprüche),
//            dreifache Schadensberechnung, § 141 PatG (Verjährung),
//            Prozess: § 143 PatG, einstweilige Verfügung §§ 935–945 ZPO,
//            Aussetzung § 148 ZPO, Trennungsprinzip, Berechtigungsanfrage, Schutzschrift.
// Quellen: Termine 08 + 09 (Vincents Lernfassungen, local-only), Gesetzestext.
// IDs: da-verletzung-001 … da-verletzung-041
// reviewStatus "draft" — Freigabe ausschließlich durch question-reviewer.
export const ausbildungVerletzung: LearningExercise[] = [

  // ─── FLASHCARDS (8) ─────────────────────────────────────────────────────────

  {
    id: "da-verletzung-001",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Verletzungsrecht - Schutzbereich",
    title: "§ 14 PatG — Schutzbereich und Auslegung",
    prompt:
      "Was bestimmt § 14 PatG zum Schutzbereich eines Patents, und welche Rolle spielen Beschreibung und Zeichnungen?",
    options: [],
    answers: [],
    explanation:
      "§ 14 PatG: Der Schutzbereich des Patents wird durch die Patentansprüche bestimmt. Beschreibung und Zeichnungen sind nur zur Auslegung der Patentansprüche heranzuziehen. Entsprechend Art. 69 EPÜ i.V.m. dem Auslegungsprotokoll ist weder ein streng wörtliches (penibel-literales) noch ein rein auf den Erfindungsgedanken abstellendes (zentrales) Verständnis maßgeblich, sondern eine Auslegung, die dem Anspruchswortlaut als Ausgangspunkt billigen Schutz und angemessene Rechtssicherheit gewährt.",
    sourceIds: ["patg14", "art69epc"],
    sources: [
      { id: "patg14", label: "§ 14 PatG", url: "https://www.gesetze-im-internet.de/patg/__14.html" },
      { id: "art69epc", label: "Art. 69 EPÜ", url: "https://www.epo.org/law-practice/legal-texts/html/epc/2016/d/ar69.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["verletzung", "schutzbereich", "auslegung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-002",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Verletzungsrecht - Äquivalenz",
    title: "Äquivalenzlehre: die drei BGH-Schneidmesser-Kriterien",
    prompt:
      "Nennen Sie die drei Gleichwertigkeitskriterien der BGH-Äquivalenzlehre (Leitentscheidung 'Schneidmesser') und das vierte Korrektiv.",
    options: [],
    answers: [],
    explanation:
      "Nach BGH 'Schneidmesser I' (GRUR 2002, 511) liegt eine äquivalente Verletzung vor, wenn das abweichende Mittel (1) dieselbe Wirkung erzielt wie das Merkmal im Anspruch (Gleichwirkung), (2) für den Fachmann am Prioritätstag auffindbar war (objektive Auffindbarkeit) und (3) im Hinblick auf den Erfindungsgedanken als gleichwertig anzusehen ist (Gleichwertigkeit). Viertes Korrektiv: Die Lösung muss sich dem Fachmann am Anspruch orientieren — wer bewusst auf das patentierte Merkmal verzichtet, erhält keinen Äquivalenzschutz (Orientierung am Patentanspruch als Maßstab der Rechtssicherheit, § 14 PatG).",
    sourceIds: ["patg14"],
    sources: [
      { id: "patg14", label: "§ 14 PatG", url: "https://www.gesetze-im-internet.de/patg/__14.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["verletzung", "aequivalenz", "schutzbereich"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-003",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Verletzungsrecht - § 9 PatG",
    title: "Verbietungsrechte: Benutzungshandlungen nach § 9 PatG",
    prompt:
      "Welche Benutzungshandlungen kann der Patentinhaber nach § 9 S. 2 PatG Dritten verbieten (Erzeugnispatent)?",
    options: [],
    answers: [],
    explanation:
      "§ 9 S. 2 PatG: Beim Erzeugnispatent sind verboten (ohne Zustimmung des Patentinhabers): Herstellen, Anbieten, Inverkehrbringen, Gebrauchen sowie Einführen oder Besitzen zu diesen Zwecken. Die Aufzählung ist abschließend. Beim Verfahrenspatent (§ 9 S. 2 Nr. 2 und 3 PatG) erfasst das Verbietungsrecht auch die Benutzung des Verfahrens sowie das Anbieten/Inverkehrbringen/Gebrauchen/Einführen/Besitzen von unmittelbar durch das Verfahren hergestellten Erzeugnissen.",
    sourceIds: ["patg9"],
    sources: [
      { id: "patg9", label: "§ 9 PatG", url: "https://www.gesetze-im-internet.de/patg/__9.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["verletzung", "benutzungshandlung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-004",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Verletzungsrecht - § 12 PatG",
    title: "Vorbenutzungsrecht (§ 12 PatG)",
    prompt:
      "Was setzt das Vorbenutzungsrecht nach § 12 PatG voraus, und welchen Umfang hat es?",
    options: [],
    answers: [],
    explanation:
      "§ 12 Abs. 1 S. 1 PatG: Wer zur Zeit der Anmeldung im Inland die Erfindung bereits in Benutzung genommen oder die dazu erforderlichen Veranstaltungen getroffen hatte, ist befugt, die Erfindung für die Bedürfnisse seines eigenen Betriebs in eigenen oder fremden Werkstätten zu benutzen. Die Befugnis kann nur zusammen mit dem Betrieb vererbt oder veräußert werden (§ 12 Abs. 1 S. 2 PatG). Maßgeblicher Zeitpunkt ist die Anmeldung; nimmt der Patentinhaber eine Priorität in Anspruch, tritt nach § 12 Abs. 2 S. 1 PatG die frühere (Prioritäts-)Anmeldung an deren Stelle — der Vorbenutzer muss die Benutzung also bis zum Prioritätstag aufgenommen haben. Die Vorbenutzung muss im Inland erfolgt sein.",
    sourceIds: ["patg12"],
    sources: [
      { id: "patg12", label: "§ 12 PatG", url: "https://www.gesetze-im-internet.de/patg/__12.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["verletzung", "vorbenutzung", "schranken"],
    // reviewed (Orchestrator-Override 2026-06-03): § 12 PatG verbatim am Gesetzestext geprüft (gesetze-im-internet.de/patg/__12.html). § 12 Abs. 2 S. 1 lautet: "Steht dem Patentinhaber ein Prioritätsrecht zu, so ist an Stelle der in Absatz 1 bezeichneten Anmeldung die frühere Anmeldung maßgebend." = Prioritätsregel (Karte korrekt). Die 6-Monats-Mitteilungsregel steht in § 12 Abs. 1 (letzter Satz), NICHT in Abs. 2 — das Re-Gate hatte die Absätze verwechselt. Inhalt bestätigt.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-005",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Verletzungsrecht - Schadensersatz",
    title: "Dreifache Schadensberechnung (§ 139 Abs. 2 PatG)",
    prompt:
      "Welche drei Berechnungsmethoden stehen dem Verletzten nach § 139 Abs. 2 PatG zur Wahl, und wann muss er die Wahl treffen?",
    options: [],
    answers: [],
    explanation:
      "§ 139 Abs. 2 PatG: Bei Verschulden (Vorsatz oder Fahrlässigkeit) kann der Verletzte Schadensersatz verlangen. Der BGH erkennt drei Berechnungsmethoden: (1) konkreter Schaden (insbesondere entgangener Gewinn), (2) Lizenzanalogie — die angemessene Lizenzgebühr, die vernünftige Parteien vereinbart hätten (BGH 'Druckmaschinen-Temperierungssystem'), (3) Herausgabe des Verletzergewinns (nur der kausal durch die Verletzung erzielte Anteil, BGH 'Faxkarte'). Die Wahl erfolgt nach Erhalt der Auskunft (§ 140b PatG) und ist endgültig.",
    sourceIds: ["patg139", "patg140b"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
      { id: "patg140b", label: "§ 140b PatG", url: "https://www.gesetze-im-internet.de/patg/__140b.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["verletzung", "schadensersatz", "lizenzanalogie"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-006",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Verletzungsrecht - Prozess",
    title: "Trennungsprinzip: Verletzungsgericht vs. BPatG",
    prompt:
      "Was bedeutet das Trennungsprinzip im deutschen Patentrecht, und wie reagiert das Verletzungsgericht auf eine parallele Nichtigkeitsklage?",
    options: [],
    answers: [],
    explanation:
      "Das Trennungsprinzip besagt: Das Verletzungsgericht (Landgericht nach § 143 PatG) prüft ausschließlich, ob das Patent verletzt wird; die Nichtigkeit des Patents prüft das Bundespatentgericht (BPatG). Das Verletzungsgericht kann, wenn eine Nichtigkeitsklage (§ 81 PatG) oder ein Einspruch anhängig ist, das Verletzungsverfahren nach § 148 ZPO aussetzen. Die Aussetzung liegt im pflichtgemäßen Ermessen des Gerichts; sie setzt voraus, dass der Nichtigkeitsangriff hinreichende Erfolgsaussichten hat. Eine Einrede der Nichtigkeit im Verletzungsverfahren unmittelbar ist nicht möglich.",
    sourceIds: ["patg143", "patg139"],
    sources: [
      { id: "patg143", label: "§ 143 PatG", url: "https://www.gesetze-im-internet.de/patg/__143.html" },
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["verletzung", "prozess", "trennungsprinzip"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-007",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Verletzungsrecht - Prozess",
    title: "Einstweilige Verfügung im Patentrecht: Verfügungsanspruch und -grund",
    prompt:
      "Welche zwei Voraussetzungen müssen für eine einstweilige Verfügung im Patentverletzungsfall glaubhaft gemacht werden, und wie wird Dringlichkeit verwirkt?",
    options: [],
    answers: [],
    explanation:
      "Nach §§ 935 ff. ZPO setzt die einstweilige Verfügung voraus: (1) Verfügungsanspruch — glaubhaft gemachter materieller Anspruch (z.B. Unterlassungsanspruch nach § 139 Abs. 1 PatG), typisch durch eidesstattliche Versicherung und Vorlage der Patentschrift; (2) Verfügungsgrund — Dringlichkeit (§ 935 ZPO), d.h. ohne sofortigen Rechtsschutz würde die Rechtsdurchsetzung vereitelt oder wesentlich erschwert. Im Patentrecht ist Dringlichkeit nicht gesetzlich vermutet (anders als im UWG). Wer nach Kenntnis der Verletzung mehr als ca. 1 Monat zuwartet, verwirkt die Dringlichkeit (BGH 'Farbig gestaltete Telefonkarte'). Die Vollziehungsfrist beträgt 1 Monat ab Erlass (§ 929 Abs. 2 ZPO).",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["verletzung", "einstweilige-verfuegung", "prozess"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-008",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Verletzungsrecht - Verjährung",
    title: "Verjährung patentrechtlicher Ansprüche (§ 141 PatG)",
    prompt:
      "Wie lange verjähren patentrechtliche Schadensersatzansprüche, und ab wann beginnt die Frist zu laufen?",
    options: [],
    answers: [],
    explanation:
      "§ 141 PatG i.V.m. §§ 195, 199 BGB: Die regelmäßige Verjährungsfrist beträgt 3 Jahre. Die Frist beginnt gemäß § 199 Abs. 1 BGB mit dem Schluss des Jahres, in dem der Anspruch entstanden ist und der Gläubiger Kenntnis von Verletzungshandlung und Person des Verletzers erlangt hat (oder ohne grobe Fahrlässigkeit hätte erlangen müssen). Ohne Kenntnis verjähren Ansprüche nach § 199 Abs. 3 BGB spätestens in 10 Jahren ab Entstehung. Der Unterlassungsanspruch entsteht mit jeder Verletzungshandlung neu.",
    sourceIds: ["patg141"],
    sources: [
      { id: "patg141", label: "§ 141 PatG", url: "https://www.gesetze-im-internet.de/patg/__141.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["verletzung", "verjährung", "prozess"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE (14) ─────────────────────────────────────────────────────────

  {
    id: "da-verletzung-009",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - § 9 PatG",
    title: "Verschulden beim Unterlassungsanspruch",
    prompt:
      "Patentinhaber P stellt fest, dass Konkurrent K sein Patent benutzt, ohne davon zu wissen. Kann P Unterlassung nach § 139 Abs. 1 PatG verlangen?",
    options: [
      "Nein, § 139 PatG setzt stets Vorsatz oder Fahrlässigkeit voraus.",
      "Ja, der Unterlassungsanspruch ist verschuldensunabhängig; Schadensersatz erfordert dagegen Verschulden.",
      "Ja, aber nur wenn K trotz Kenntnis die Verletzung fortsetzt.",
      "Nein, zuerst muss eine Verwarnung ausgesprochen und ignoriert worden sein.",
    ],
    answers: [1],
    explanation:
      "§ 139 Abs. 1 PatG: Der Unterlassungsanspruch setzt keine Schuld voraus — jede objektiv rechtswidrige Benutzung begründet ihn. Nur der Schadensersatzanspruch nach § 139 Abs. 2 PatG erfordert Vorsatz oder Fahrlässigkeit. Ebenso wenig ist eine vorherige Verwarnung Voraussetzung für die Klage (wohl aber kostenrechtlich relevant nach § 93 ZPO).",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Abgrenzung",
    skillTags: ["verletzung", "unterlassung", "verschulden"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-010",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - § 11 PatG",
    title: "Schranken: Privater Gebrauch und Versuchsprivileg",
    prompt:
      "Forschungsinstitut F benutzt eine patentierte Verbindung ausschließlich zu wissenschaftlichen Versuchszwecken, ohne Ergebnisse zu verwerten. Greift ein Erlaubnistatbestand?",
    options: [
      "Nein, jede Benutzung ohne Zustimmung ist eine Verletzung nach § 9 PatG.",
      "Ja, § 11 Nr. 2 PatG erlaubt Handlungen zu Versuchszwecken, die sich auf den Gegenstand der patentierten Erfindung beziehen.",
      "Ja, aber nur wenn F die Ergebnisse veröffentlicht.",
      "Nein, das Versuchsprivileg gilt nur für natürliche Personen, nicht für Institute.",
    ],
    answers: [1],
    explanation:
      "§ 11 Nr. 2 PatG: Handlungen zu Versuchszwecken, die sich auf den Gegenstand der patentierten Erfindung beziehen, sind von der Wirkung des Patents ausgenommen. Der Versuchszweck muss auf die Erfindung selbst gerichtet sein (z.B. Prüfung ihrer Eigenschaften), nicht auf einen fremden Gegenstand. Eine Verwertungspflicht besteht nicht. Die Norm gilt für jedermann, unabhängig von Rechtsform.",
    sourceIds: ["patg11"],
    sources: [
      { id: "patg11", label: "§ 11 PatG", url: "https://www.gesetze-im-internet.de/patg/__11.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Subsumtion",
    skillTags: ["verletzung", "schranken", "versuchsprivileg"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-011",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - Schutzbereich",
    title: "Auslegung: Protokoll zu Art. 69 EPÜ",
    prompt:
      "Das Auslegungsprotokoll zu Art. 69 EPÜ bestimmt, welche Auslegungsmethode für den Schutzbereich maßgeblich ist. Welche Aussage trifft zu?",
    options: [
      "Der Anspruchswortlaut ist absolut bindend; jede Abweichung schließt Schutz aus (peripheres Verständnis).",
      "Der Schutzbereich ist identisch mit dem allgemeinen Erfindungsgedanken, unabhängig vom Wortlaut (zentrales Verständnis).",
      "Weder rein wörtliche noch rein zentralistische Auslegung: Der Anspruch dient als Leitlinie; Beschreibung und Zeichnungen helfen bei der Auslegung.",
      "Schutz besteht nur für im Anspruch ausdrücklich genannte Ausführungsbeispiele.",
    ],
    answers: [2],
    explanation:
      "Das Auslegungsprotokoll zu Art. 69 EPÜ (umgesetzt in § 14 PatG) verwirft sowohl das streng literale (Art. 69 Abs. 1 S. 2 EPÜ wäre auf den Wortlaut als Mindest/Maximal-Schutz reduziert) als auch das zentralistische Verständnis (Schutzbereich gleich allgemeiner Erfindungsgedanke). Maßgeblich ist ein ausgewogener Mittelweg: der Patentanspruch ist Ausgangspunkt, Beschreibung und Zeichnungen dienen der Auslegung (§ 14 PatG), nicht der Erweiterung oder Einschränkung.",
    sourceIds: ["patg14", "art69epc"],
    sources: [
      { id: "patg14", label: "§ 14 PatG", url: "https://www.gesetze-im-internet.de/patg/__14.html" },
      { id: "art69epc", label: "Art. 69 EPÜ", url: "https://www.epo.org/law-practice/legal-texts/html/epc/2016/d/ar69.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["verletzung", "schutzbereich", "auslegung"],
    // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-012",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - § 10 PatG",
    title: "Mittelbare Patentverletzung (§ 10 PatG)",
    prompt:
      "Lieferant L verkauft an Hersteller H Bauteile, die ausschließlich zur Benutzung einer patentierten Maschine geeignet sind. L weiß, dass H die Teile zur Patentverletzung einsetzt. Welche Norm schützt Patentinhaber P gegen L?",
    options: [
      "§ 9 PatG, weil L die Benutzungshandlung 'Inverkehrbringen' vornimmt.",
      "§ 10 Abs. 1 PatG, weil L Mittel, die sich auf ein wesentliches Element der Erfindung beziehen, an nicht berechtigte Personen liefert und dies weiß.",
      "§ 139 PatG direkt, ohne dass eine eigene Benutzungshandlung nach § 9 PatG vorliegen muss.",
      "Keine patentrechtliche Norm — P muss auf Wettbewerbsrecht (UWG) zurückgreifen.",
    ],
    answers: [1],
    explanation:
      "§ 10 Abs. 1 PatG: Die Lieferung oder das Anbieten von Mitteln, die sich auf ein wesentliches Element der Erfindung beziehen, an nicht zur Benutzung Berechtigte ist verboten, wenn der Lieferant weiß oder es auf der Hand liegt, dass die Mittel dazu geeignet und bestimmt sind, zur Benutzung der Erfindung verwendet zu werden. Dies ist die mittelbare Patentverletzung. § 9 PatG erfasst nur unmittelbare Benutzungshandlungen; § 10 PatG schließt die Lücke für Zulieferer.",
    sourceIds: ["patg10"],
    sources: [
      { id: "patg10", label: "§ 10 PatG", url: "https://www.gesetze-im-internet.de/patg/__10.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["verletzung", "mittelbare-verletzung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-013",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - Ansprüche",
    title: "Vernichtung und Rückruf (§ 140a PatG)",
    prompt:
      "Patentinhaber P hat einen vollstreckbaren Unterlassungstitel. Welche weiteren sachenrechtlichen Ansprüche kann er nach § 140a PatG geltend machen?",
    options: [
      "Nur Vernichtung der beim Verletzer vorhandenen patentverletzenden Erzeugnisse.",
      "Vernichtung (§ 140a Abs. 1 PatG) und Rückruf aus Vertriebswegen (§ 140a Abs. 3 PatG); in beiden Fällen auf Kosten des Verletzers.",
      "Rückruf aus dem Markt ist nur bei Verbrauchsgütern, nicht bei Industrieprodukten möglich.",
      "§ 140a PatG greift nur, wenn der Verletzer in einem gewerblichen Maßstab gehandelt hat.",
    ],
    answers: [1],
    explanation:
      "§ 140a Abs. 1 PatG: Der Inhaber des Patentrechts kann vom Verletzer Vernichtung der im Besitz oder Eigentum des Verletzers befindlichen patentverletzenden Erzeugnisse verlangen. § 140a Abs. 3 PatG: Der Verletzer kann auch zum Rückruf patentverletzender Erzeugnisse aus den Vertriebswegen verpflichtet werden. Beide Ansprüche entfallen, wenn sie im Einzelfall unverhältnismäßig sind. Ein Unterlassungstitel ist keine zwingende Voraussetzung; § 140a PatG ist ein eigenständiger Anspruch.",
    sourceIds: ["patg140a"],
    sources: [
      { id: "patg140a", label: "§ 140a PatG", url: "https://www.gesetze-im-internet.de/patg/__140a.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["verletzung", "vernichtung", "rueckruf"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-014",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - Prozess",
    title: "Gerichtsstand und sachliche Zuständigkeit (§ 143 PatG)",
    prompt:
      "In welchem Gerichtszweig und bei welchem Gericht ist eine Patentverletzungsklage einzureichen?",
    options: [
      "Beim Amtsgericht, örtlich bestimmt durch den allgemeinen Gerichtsstand (Wohnsitz des Beklagten).",
      "Beim Landgericht; örtlich nach § 32 ZPO (Tatort) oder allgemeinem Gerichtsstand, sachlich konzentriert nach § 143 PatG bei bestimmten LGs.",
      "Beim Bundespatentgericht, das für alle Patentsachen ausschließlich zuständig ist.",
      "Beim OLG als erster Instanz, weil der Streitwert bei Patentsachen regelmäßig über 5.000 € liegt.",
    ],
    answers: [1],
    explanation:
      "§ 143 PatG: Für Klagen aus dem Patentgesetz sind die Landgerichte zuständig; die Landesregierungen können die Zuständigkeit bei einem LG im Land konzentrieren (z.B. LG Düsseldorf, LG München I, LG Mannheim). Örtlich gilt § 32 ZPO (Tatort-Gerichtsstand für Delikte) oder §§ 12, 17 ZPO (allgemeiner Gerichtsstand). Das BPatG ist für Nichtigkeitsklagen zuständig, nicht für Verletzungsklagen (Trennungsprinzip).",
    sourceIds: ["patg143"],
    sources: [
      { id: "patg143", label: "§ 143 PatG", url: "https://www.gesetze-im-internet.de/patg/__143.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["verletzung", "prozess", "zustaendigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-015",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - Erschöpfung",
    title: "Erschöpfung des Patentrechts",
    prompt:
      "Patentinhaber P verkauft patentgeschützte Geräte an Händler H im EWR. Händler H veräußert die Geräte weiter an Endkunden. Kann P den Weiterverkauf durch H nach § 9 PatG verbieten?",
    options: [
      "Ja, jeder Weiterverkauf ohne neue Lizenz ist eine Verletzung.",
      "Nein, das Patentrecht erschöpft sich mit dem ersten Inverkehrbringen im EWR durch den Rechtsinhaber oder mit seiner Zustimmung.",
      "Nein, aber nur wenn H im selben Land wie P verkauft.",
      "Ja, weil die Erschöpfung nur das Urheberrecht, nicht das Patentrecht betrifft.",
    ],
    answers: [1],
    explanation:
      "Der patentrechtliche Erschöpfungsgrundsatz ist nicht ausdrücklich kodifiziert, sondern gewohnheits-/richterrechtlich anerkannt; die EWR-weite Erschöpfung folgt aus der Rechtsprechung des EuGH zur Warenverkehrsfreiheit (Art. 34–36 AEUV). Hat der Patentinhaber — oder ein Dritter mit seiner Zustimmung — das patentgeschützte Erzeugnis erstmals im EWR in Verkehr gebracht, sind seine Verbietungsrechte aus § 9 PatG für dieses konkrete Exemplar erschöpft. Der Weiterverkauf durch H ist damit patentrechtlich erlaubt. Eine Erschöpfung durch Inverkehrbringen außerhalb des EWR tritt grundsätzlich nicht ein.",
    sourceIds: ["patg9"],
    sources: [
      { id: "patg9", label: "§ 9 PatG", url: "https://www.gesetze-im-internet.de/patg/__9.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["verletzung", "erschoepfung", "schutzbereich"],
    // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-016",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - Schadensersatz",
    title: "Lizenzanalogie: BGH 'Druckmaschinen-Temperierungssystem'",
    prompt:
      "Wie bestimmt sich die Lizenzgebühr bei der Schadensersatzberechnung nach Lizenzanalogie?",
    options: [
      "Es wird die tatsächlich vereinbarte Lizenzgebühr des Verletzers mit einem Dritten zugrunde gelegt.",
      "Es wird die hypothetische angemessene Lizenzgebühr herangezogen, auf die sich vernünftige Vertragspartner im Zeitpunkt der Verletzungshandlung geeinigt hätten.",
      "Es wird der durchschnittliche Marktpreis des verletzenden Produkts angesetzt.",
      "Es wird der entgangene Gewinn des Patentinhabers pro verkauftem Stück multipliziert mit der Anzahl der Verletzungshandlungen berechnet.",
    ],
    answers: [1],
    explanation:
      "Nach § 139 Abs. 2 PatG und BGH 'Druckmaschinen-Temperierungssystem' (GRUR 2010, 239) ist bei der Lizenzanalogie maßgeblich, welche Vergütung angemessene Vertragspartner bei Abschluss eines Lizenzvertrages vereinbart hätten. Es handelt sich um eine objektive Beurteilung — nicht die konkrete Vereinbarung des Verletzers mit Dritten, sondern die hypothetische Einigung vernünftiger Parteien. Branchenübliche Sätze, Exklusivität und wirtschaftlicher Nutzen fließen ein.",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["verletzung", "schadensersatz", "lizenzanalogie"],
    // REVIEW-FIX (nicht blockierend): Fundstelle prüfen — BGH "Druckmaschinen-Temperierungssystem" ist X ZR 175/07, GRUR 2010, 239 (nicht 223). Sache/Grundsatz korrekt.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-017",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - Prozess",
    title: "Unberechtigte Verwarnung",
    prompt:
      "Hersteller H mahnt Konkurrent K wegen Verletzung eines Patents ab, das bereits erloschen ist. Welche Konsequenz droht H?",
    options: [
      "Keine — eine Abmahnung begründet nie eigene Haftung des Mahnenden.",
      "H begeht eine unberechtigte Verwarnung: K kann Schadensersatz nach § 823 Abs. 1 BGB und/oder einen Unterlassungsanspruch nach UWG geltend machen.",
      "H verliert automatisch alle noch bestehenden Schutzrechte.",
      "K kann beim DPMA die sofortige Löschung aller Patente von H beantragen.",
    ],
    answers: [1],
    explanation:
      "Die unberechtigte Schutzrechtsverwarnung stellt einen Eingriff in den eingerichteten und ausgeübten Gewerbebetrieb dar (§ 823 Abs. 1 BGB) sowie eine unlautere geschäftliche Handlung (§§ 3, 4 Nr. 4 UWG). H muss prüfen, ob das Schutzrecht in Kraft ist und tatsächlich verletzt wird — andernfalls haftet er auf Schadensersatz. Die Abmahnung löst auch ohne Klage Kosten und Haftungsrisiken aus.",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["verletzung", "verwarnung", "prozess"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-018",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - Äquivalenz",
    title: "Äquivalenz: Auffindbarkeit für den Fachmann",
    prompt:
      "Patentanspruch 1 nennt Merkmal X. Produkt P des Verletzers enthält stattdessen Merkmal Y, das dieselbe technische Wirkung erzielt. Welches Kriterium entscheidet über äquivalenten Schutz?",
    options: [
      "Ob Merkmal Y im Erteilungsverfahren ausdrücklich aus dem Anspruch gestrichen wurde.",
      "Ob der Fachmann am Prioritätstag Merkmal Y als gleichwirkendes Austauschmittel aufgefunden hätte und es als gleichwertig zur Lösung des der Erfindung zugrundeliegenden Problems ansieht.",
      "Ob Merkmal Y in der Beschreibung des Patents erwähnt wird.",
      "Ob Merkmal Y zum Zeitpunkt der Verletzung neu auf dem Markt war.",
    ],
    answers: [1],
    explanation:
      "Nach BGH 'Schneidmesser I' (GRUR 2002, 511) setzt äquivalenter Schutz voraus: (1) Gleichwirkung von Y zu X, (2) Auffindbarkeit für den Fachmann am Prioritätstag (objektive Erkennbarkeit) und (3) Gleichwertigkeit im Hinblick auf den Erfindungsgedanken (§ 14 PatG). Die bloße Erwähnung in der Beschreibung oder eine Marktneuheit sind für sich allein nicht entscheidend. Das Auffindbarkeits-Kriterium stellt auf den Stand der Technik am Prioritätstag ab.",
    sourceIds: ["patg14"],
    sources: [
      { id: "patg14", label: "§ 14 PatG", url: "https://www.gesetze-im-internet.de/patg/__14.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["verletzung", "aequivalenz", "schutzbereich"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-019",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - Prozess",
    title: "§ 93 ZPO — Sofortiges Anerkenntnis und Kostenfolge",
    prompt:
      "P klagt auf Unterlassung, ohne zuvor eine Verwarnung ausgesprochen zu haben. K erkennt die Klage sofort an. Wer trägt die Prozesskosten?",
    options: [
      "K trägt die Kosten, weil er die Verletzung begangen hat.",
      "P trägt die Kosten nach § 93 ZPO, weil K keinen Anlass zur Klageerhebung gegeben (mangels vorheriger Abmahnung) und die Klage sofort anerkannt hat.",
      "Kosten werden hälftig geteilt (§ 92 ZPO).",
      "Der Streitwert entscheidet, wer die Kosten trägt.",
    ],
    answers: [1],
    explanation:
      "§ 93 ZPO: Erkennt der Beklagte sofort an, so fallen die Kosten dem Kläger zur Last, wenn der Beklagte nicht durch sein Verhalten Anlass zur Klageerhebung gegeben hat. Ohne vorherige Abmahnung hat K keine Möglichkeit gehabt, die Verletzung freiwillig einzustellen — er hat damit keinen Anlass zur Klage gegeben. Die Verwarnung ist zwar keine patentrechtliche Pflicht, aber kostenprozessual bedeutsam.",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["verletzung", "prozess", "kosten"],
    // REVIEW-FIX (nicht blockierend): In Option B Klammerzusatz "(kein Verzug ...)" begrifflich unscharf — § 93 ZPO stellt auf "keinen Anlass zur Klage gegeben" ab, nicht auf Verzug. Ergebnis und Erklärung korrekt.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-020",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - Ansprüche",
    title: "Auskunftsanspruch (§ 140b PatG): Voraussetzungen",
    prompt:
      "Wann kann der Patentinhaber Auskunft nach § 140b PatG verlangen?",
    options: [
      "Nur nach rechtskräftiger Verurteilung des Verletzers wegen Schadensersatz.",
      "Bei offensichtlicher Patentverletzung oder nach Klageerhebung; Inhalt: Herkunft und Vertriebsweg der verletzenden Erzeugnisse sowie Art und Umfang der Benutzungshandlungen.",
      "Nur wenn der Verletzer gewerblich handelt und einen Jahresumsatz über 1 Mio. € erzielt.",
      "Ausschließlich im Rahmen eines selbstständigen Beweisverfahrens nach §§ 485 ff. ZPO.",
    ],
    answers: [1],
    explanation:
      "§ 140b Abs. 1 PatG: Bei offensichtlicher Rechtsverletzung oder nach Klageerhebung kann der Verletzte Auskunft über Herkunft und Vertriebsweg der patentverletzenden Erzeugnisse sowie Art und Umfang der Benutzungshandlungen verlangen. Die Auskunft ist Vorstufe zur konkreten Schadensberechnung (Lizenzanalogie, Verletzergewinn). Weder eine Verurteilung noch ein Mindestumsatz ist Voraussetzung.",
    sourceIds: ["patg140b"],
    sources: [
      { id: "patg140b", label: "§ 140b PatG", url: "https://www.gesetze-im-internet.de/patg/__140b.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["verletzung", "auskunft", "prozess"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-021",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - Prozess",
    title: "Schutzschrift im einstweiligen Verfügungsverfahren",
    prompt:
      "Warum hinterlegt ein potenzieller Antragsgegner eine Schutzschrift, und was ist ihr rechtlicher Charakter?",
    options: [
      "Eine Schutzschrift ist eine vorläufige Klageerwiderung, die nur nach Einreichung einer Klage zulässig ist.",
      "Eine Schutzschrift ist ein vorbeugend beim Gericht hinterlegtes Dokument, das dem Antragsgegner rechtliches Gehör sichern soll, bevor eine ex-parte-Verfügung ergeht.",
      "Eine Schutzschrift ersetzt das Widerspruchsverfahren gegen die einstweilige Verfügung vollständig.",
      "Eine Schutzschrift kann nur beim Bundespatentgericht, nicht bei den Verletzungsgerichten eingereicht werden.",
    ],
    answers: [1],
    explanation:
      "Die Schutzschrift ist ein vorbeugendes Verteidigungsmittel: Der potenzielle Antragsgegner hinterlegt sein Vorbringen vorab beim zentralen Schutzschriftenregister (§§ 945a, 945b ZPO), damit das Gericht es berücksichtigt, bevor es eine einstweilige Verfügung ohne mündliche Verhandlung (ex parte) erlässt. Sie dient der Wahrung des rechtlichen Gehörs (Art. 103 Abs. 1 GG) bzw. der prozessualen Waffengleichheit. Sie ersetzt nicht den Widerspruch nach § 924 ZPO, der erst nach Erlass der Beschlussverfügung statthaft ist.",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Definition",
    skillTags: ["verletzung", "prozess", "einstweilige-verfuegung"],
    // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-022",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - § 12 PatG",
    title: "Vorbenutzungsrecht: Übertragbarkeit",
    prompt:
      "Maschinenbauer M hat ein Vorbenutzungsrecht nach § 12 PatG für sein Produkt. Er verkauft seinen gesamten Betrieb an Käufer K. Was gilt für das Vorbenutzungsrecht?",
    options: [
      "Das Vorbenutzungsrecht erlischt mit dem Betriebsübergang.",
      "Das Vorbenutzungsrecht geht mit dem Betrieb auf K über und kann nur zusammen mit diesem übertragen werden.",
      "K muss eine neue Vorbenutzung nachweisen, da Rechte höchstpersönlich sind.",
      "Das Vorbenutzungsrecht kann auch separat, ohne Betriebsübergang, abgetreten werden.",
    ],
    answers: [1],
    explanation:
      "§ 12 Abs. 1 S. 2 PatG: Die Befugnis aus dem Vorbenutzungsrecht kann nur zusammen mit dem Betrieb, für dessen Bedürfnisse sie besteht, vererbt oder veräußert werden. Mit dem Verkauf des gesamten Betriebs geht das Recht auf K über. Eine isolierte Abtretung ohne den Betrieb ist unzulässig.",
    sourceIds: ["patg12"],
    sources: [
      { id: "patg12", label: "§ 12 PatG", url: "https://www.gesetze-im-internet.de/patg/__12.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Subsumtion",
    skillTags: ["verletzung", "vorbenutzung", "schranken"],
    // REVIEW-FIX (nicht blockierend): Zitat korrigieren — Nichtübertragbarkeit/Betriebsbindung steht in § 12 Abs. 1 S. 2 PatG, nicht "§ 12 Abs. 2 PatG" (§ 12 hat nur einen Absatz). Sache korrekt.
    reviewStatus: "beta-ready",
  },

  // ─── MC MULTI (8) ───────────────────────────────────────────────────────────

  {
    id: "da-verletzung-023",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "Verletzungsrecht - § 9 PatG",
    title: "Welche Benutzungshandlungen verbietbarer Natur? (Mehrfachauswahl)",
    prompt:
      "Welche der folgenden Handlungen sind bei einem Erzeugnispatent ohne Zustimmung des Patentinhabers nach § 9 S. 2 Nr. 1 PatG verboten? (Mehrere Antworten möglich)",
    options: [
      "Herstellen des patentgeschützten Erzeugnisses",
      "Anbieten des patentgeschützten Erzeugnisses",
      "Privates, nicht gewerbliches Benutzen des Erzeugnisses zu persönlichen Zwecken",
      "Einführen des patentgeschützten Erzeugnisses aus einem Drittland",
    ],
    answers: [0, 1, 3],
    explanation:
      "§ 9 S. 2 Nr. 1 PatG: Verboten sind Herstellen, Anbieten, Inverkehrbringen, Gebrauchen sowie Einführen und Besitzen zu diesen Zwecken. Der private, nicht gewerbliche Gebrauch (§ 11 Nr. 1 PatG) ist hingegen als Schranke vom Verbot ausgenommen. Einführen aus Drittländern fällt unter das Verbot (kein EWR-Erschöpfungsprinzip bei Import von außen).",
    sourceIds: ["patg9", "patg11"],
    sources: [
      { id: "patg9", label: "§ 9 PatG", url: "https://www.gesetze-im-internet.de/patg/__9.html" },
      { id: "patg11", label: "§ 11 PatG", url: "https://www.gesetze-im-internet.de/patg/__11.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["verletzung", "benutzungshandlung", "schranken"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-024",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "Verletzungsrecht - Ansprüche",
    title: "Ansprüche des Verletzten: korrekte Zuordnung (Mehrfachauswahl)",
    prompt:
      "Welche der folgenden Ansprüche stehen dem Patentinhaber nach dem PatG bei einer festgestellten Patentverletzung zu? (Mehrere Antworten möglich)",
    options: [
      "Unterlassung (§ 139 Abs. 1 PatG)",
      "Schadensersatz bei Verschulden (§ 139 Abs. 2 PatG)",
      "Vernichtung patentverletzender Erzeugnisse (§ 140a Abs. 1 PatG)",
      "Strafanzeige und direkte Strafverfolgung durch den Patentinhaber (§ 142 PatG)",
    ],
    answers: [0, 1, 2],
    explanation:
      "§ 139 Abs. 1 PatG: Unterlassungsanspruch (verschuldensunabhängig). § 139 Abs. 2 PatG: Schadensersatz (bei Vorsatz oder Fahrlässigkeit). § 140a Abs. 1 PatG: Vernichtungsanspruch. § 142 PatG regelt die Strafbarkeit (Freiheits- oder Geldstrafe), aber die Strafverfolgung liegt beim Staat (Staatsanwaltschaft), nicht beim Patentinhaber — er kann nur Strafanzeige erstatten. Eine direkte Strafverfolgung durch den Verletzten ist kein zivilrechtlicher Anspruch.",
    sourceIds: ["patg139", "patg140a", "patg142"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
      { id: "patg140a", label: "§ 140a PatG", url: "https://www.gesetze-im-internet.de/patg/__140a.html" },
      { id: "patg142", label: "§ 142 PatG", url: "https://www.gesetze-im-internet.de/patg/__142.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["verletzung", "ansprueche"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-025",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "Verletzungsrecht - Verteidigung",
    title: "Verteidigungsmittel des Verletzers (Mehrfachauswahl)",
    prompt:
      "Welche der folgenden Einwände kann der beklagte Verletzer im Verletzungsprozess prozessual geltend machen? (Mehrere Antworten möglich)",
    options: [
      "Erschöpfungseinwand (Patent erschöpft durch EWR-weites Inverkehrbringen)",
      "Vorbenutzungsrecht nach § 12 PatG",
      "Direkte Nichtigkeitseinrede im Verletzungsverfahren, die das LG zur Einstellung zwingt",
      "Verjährungseinrede (§ 141 PatG i.V.m. §§ 195, 199 BGB)",
    ],
    answers: [0, 1, 3],
    explanation:
      "Im Verletzungsverfahren anerkannte Verteidigungsmittel: Erschöpfungseinwand (§ 9 PatG), Vorbenutzungsrecht (§ 12 PatG), Verjährung (§ 141 PatG). Eine direkte Nichtigkeitseinrede, die das Verletzungsgericht zur Verfahrenseinstellung zwingt, gibt es nicht (Trennungsprinzip). Der Beklagte kann eine Nichtigkeitsklage beim BPatG erheben und beim LG Aussetzung nach § 148 ZPO beantragen — erzwingen kann er die Aussetzung nicht.",
    sourceIds: ["patg9", "patg12", "patg141"],
    sources: [
      { id: "patg9", label: "§ 9 PatG", url: "https://www.gesetze-im-internet.de/patg/__9.html" },
      { id: "patg12", label: "§ 12 PatG", url: "https://www.gesetze-im-internet.de/patg/__12.html" },
      { id: "patg141", label: "§ 141 PatG", url: "https://www.gesetze-im-internet.de/patg/__141.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["verletzung", "prozess", "verteidigung", "trennungsprinzip"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-026",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "Verletzungsrecht - Prozess",
    title: "Einstweilige Verfügung: Was muss glaubhaft gemacht werden? (Mehrfachauswahl)",
    prompt:
      "Was muss der Antragsteller einer einstweiligen Verfügung nach §§ 935 ff. ZPO im Patentverletzungsfall glaubhaft machen? (Mehrere Antworten möglich)",
    options: [
      "Verfügungsanspruch: materieller Anspruch (z.B. § 139 Abs. 1 PatG)",
      "Verfügungsgrund: Dringlichkeit",
      "Dass der Beklagte sich der Zustellung entziehen wird",
      "Verschulden des Verletzers (Vorsatz oder Fahrlässigkeit)",
    ],
    answers: [0, 1],
    explanation:
      "§§ 935, 940 ZPO: Glaubhaft zu machen sind Verfügungsanspruch (materieller Anspruch) und Verfügungsgrund (Dringlichkeit). Der Unterlassungsanspruch nach § 139 Abs. 1 PatG ist verschuldensunabhängig — Verschulden ist keine Voraussetzung der Verfügung. Eine drohende Zustellungsvereitelung ist keine gesetzliche Voraussetzung der einstweiligen Verfügung.",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["verletzung", "einstweilige-verfuegung", "prozess"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-027",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "Verletzungsrecht - Schadensersatz",
    title: "Berechnungsmethoden Schadensersatz: alle drei? (Mehrfachauswahl)",
    prompt:
      "Welche der folgenden Methoden sind anerkannte Wege zur Schadensersatzberechnung nach § 139 Abs. 2 PatG? (Mehrere Antworten möglich)",
    options: [
      "Konkreter Schaden (entgangener Gewinn des Patentinhabers)",
      "Lizenzanalogie (hypothetische angemessene Lizenzgebühr)",
      "Herausgabe des Verletzergewinns",
      "Strafschadensersatz (doppelter Listenpreis des Verletzers)",
    ],
    answers: [0, 1, 2],
    explanation:
      "§ 139 Abs. 2 PatG erkennt die Rechtsprechungs-Dreiheit: (1) konkreter Schaden inkl. entgangenen Gewinn, (2) Lizenzanalogie, (3) Herausgabe des Verletzergewinns. Ein Strafschadensersatz (punitive damages) ist dem deutschen Recht fremd — § 139 Abs. 2 PatG zielt auf Kompensation, nicht auf Bestrafung.",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["verletzung", "schadensersatz"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-028",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "Verletzungsrecht - § 11 PatG",
    title: "Schranken nach § 11 PatG (Mehrfachauswahl)",
    prompt:
      "Welche der folgenden Handlungen sind nach § 11 PatG von der Patentwirkung ausgenommen? (Mehrere Antworten möglich)",
    options: [
      "Handlungen im privaten Bereich zu nicht gewerblichen Zwecken (§ 11 Nr. 1 PatG)",
      "Handlungen zu Versuchszwecken, die sich auf den Gegenstand der Erfindung beziehen (§ 11 Nr. 2 PatG)",
      "Einzelzubereitung von Arzneimitteln in der Apotheke (§ 11 Nr. 3 PatG)",
      "Großserienproduktion für den internen Bedarf eines Unternehmens",
    ],
    answers: [0, 1, 2],
    explanation:
      "§ 11 PatG nennt abschließend: Nr. 1 — privater, nicht gewerblicher Gebrauch; Nr. 2 — Versuchshandlungen; Nr. 3 — Einzelzubereitung von Arzneimitteln in der Apotheke auf ärztliche Verschreibung. Die Großserienproduktion für internen Bedarf eines Unternehmens ist gewerblich und fällt nicht unter § 11 Nr. 1 PatG — sie ist eine Verletzungshandlung nach § 9 S. 2 Nr. 1 PatG.",
    sourceIds: ["patg11"],
    sources: [
      { id: "patg11", label: "§ 11 PatG", url: "https://www.gesetze-im-internet.de/patg/__11.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["verletzung", "schranken"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-029",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "Verletzungsrecht - Prozess",
    title: "Vollstreckung des Unterlassungsurteils nach § 890 ZPO (Mehrfachauswahl)",
    prompt:
      "Welche Aussagen zur Vollstreckung eines Unterlassungsurteils nach § 890 ZPO sind richtig? (Mehrere Antworten möglich)",
    options: [
      "Das Urteil oder eine separate Androhungsverfügung muss die Androhung von Ordnungsmitteln enthalten.",
      "Ordnungsgeld beträgt bis zu 250.000 € je Verstoß; alternativ Ordnungshaft bis 6 Monate.",
      "Die Vollstreckung setzt Verschulden des Schuldners voraus.",
      "Ordnungsmittel werden automatisch verhängt, ohne Antrag des Gläubigers.",
    ],
    answers: [0, 1, 2],
    explanation:
      "§ 890 ZPO: Ordnungsgeld (bis 250.000 €) oder Ordnungshaft (bis 6 Monate) setzen voraus: (1) eine vorangegangene Androhung im Urteil oder separater Androhungsverfügung, (2) Verschulden des Schuldners (Ordnungsmittel sind Beugemittel, keine Strafe ohne Schuld), (3) Antrag des Gläubigers — sie ergehen nicht von Amts wegen.",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["verletzung", "prozess", "vollstreckung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-030",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "Verletzungsrecht - Äquivalenz",
    title: "Äquivalenz: alle drei Schneidmesser-Kriterien erfüllt? (Mehrfachauswahl)",
    prompt:
      "Produkt des Verletzers enthält Merkmal Y statt des Anspruchsmerkmals X. Welche Kriterien müssen kumulativ erfüllt sein, damit äquivalenter Schutz nach § 14 PatG besteht? (Mehrere Antworten möglich)",
    options: [
      "Y erzielt dieselbe Wirkung wie X (Gleichwirkung)",
      "Der Fachmann hätte Y am Prioritätstag als gleichwirkendes Austauschmittel auffinden können (Auffindbarkeit)",
      "Y ist aus Sicht des Fachmanns als gleichwertig anzusehen (Gleichwertigkeit/Orientierung am Erfindungsgedanken)",
      "Y muss in der Beschreibung des Patents als Alternative erwähnt sein",
    ],
    answers: [0, 1, 2],
    explanation:
      "BGH 'Schneidmesser I' (GRUR 2002, 511): Alle drei Kriterien — Gleichwirkung, Auffindbarkeit für den Fachmann am Prioritätstag, Gleichwertigkeit — müssen kumulativ vorliegen (§ 14 PatG). Eine Erwähnung in der Beschreibung ist kein eigenständiges Kriterium und begründet für sich allein weder Äquivalenz noch schließt sie diese aus.",
    sourceIds: ["patg14"],
    sources: [
      { id: "patg14", label: "§ 14 PatG", url: "https://www.gesetze-im-internet.de/patg/__14.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["verletzung", "aequivalenz", "schutzbereich"],
    reviewStatus: "beta-ready",
  },

  // ─── ORDERING (5) ───────────────────────────────────────────────────────────

  {
    id: "da-verletzung-031",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Verletzungsrecht - Prüfungsschema",
    title: "Prüfungsschema: Verletzungsklage (§§ 9, 139 PatG)",
    prompt:
      "Bringen Sie die Prüfungsschritte einer patentrechtlichen Verletzungsklage in die richtige Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "1. Schutzbereich bestimmen (§ 14 PatG / Art. 69 EPÜ): wortwörtlich + äquivalent",
      "2. Benutzungshandlung nach § 9 PatG oder § 10 PatG prüfen",
      "3. Fehlen eines Erlaubnistatbestands (§§ 11–13 PatG) und Erschöpfung prüfen",
      "4. Rechtsfolge: Unterlassung (§ 139 Abs. 1), bei Verschulden Schadensersatz (§ 139 Abs. 2), Auskunft (§ 140b), Vernichtung (§ 140a)",
    ],
    explanation:
      "Das Prüfungsschema der Patentverletzung folgt stets dieser Reihenfolge: Erst wird der Schutzbereich des Patents gemäß § 14 PatG ausgelegt (wortwörtlich und nach der Äquivalenzlehre, BGH 'Schneidmesser'). Dann wird geprüft, ob die angegriffene Ausführungsform unter § 9 PatG (unmittelbare) oder § 10 PatG (mittelbare Verletzung) fällt. Anschließend sind Schranken (§ 11 PatG: privater Gebrauch, Versuchsprivileg, Einzelzubereitung) und Erschöpfung zu prüfen. Erst dann werden die Rechtsfolgen aus §§ 139, 140a, 140b PatG erörtert.",
    sourceIds: ["patg14", "patg9", "patg11", "patg139"],
    sources: [
      { id: "patg14", label: "§ 14 PatG", url: "https://www.gesetze-im-internet.de/patg/__14.html" },
      { id: "patg9", label: "§ 9 PatG", url: "https://www.gesetze-im-internet.de/patg/__9.html" },
      { id: "patg11", label: "§ 11 PatG", url: "https://www.gesetze-im-internet.de/patg/__11.html" },
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["verletzung", "pruefungsschema"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-032",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Verletzungsrecht - Prozess",
    title: "Prüfungsschema: Einstweilige Verfügung im Patentrecht",
    prompt:
      "Ordnen Sie die Prüfungsschritte einer patentrechtlichen einstweiligen Verfügung nach §§ 935 ff. ZPO in die richtige Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "1. Verfügungsanspruch: materieller Anspruch glaubhaft machen (§ 139 Abs. 1 PatG — Patent in Kraft? Verletzungshandlung? Kein Erlaubnistatbestand?)",
      "2. Verfügungsgrund: Dringlichkeit glaubhaft machen (kein übermäßiges Zuwarten — Faustregel 1 Monat nach Kenntnis)",
      "3. Interessenabwägung: Schwere der drohenden Verletzung vs. Belastung des Antragsgegners",
      "4. Vollziehung innerhalb von 1 Monat ab Erlass (§ 929 Abs. 2 ZPO)",
    ],
    explanation:
      "Die einstweilige Verfügung im Patentrecht (§§ 935, 940 ZPO) erfordert zuerst die Glaubhaftmachung des Verfügungsanspruchs (materieller Anspruch, z.B. § 139 Abs. 1 PatG), dann des Verfügungsgrundes (Dringlichkeit — im Patentrecht nicht gesetzlich vermutet, anders als im UWG). Die Interessenabwägung ist ebenfalls zu beachten. Nach Erlass muss die Verfügung innerhalb von 1 Monat vollzogen werden (§ 929 Abs. 2 ZPO), sonst verliert sie ihre Wirkung.",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["verletzung", "einstweilige-verfuegung", "prozess"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-033",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Verletzungsrecht - Schadensersatz",
    title: "Reihenfolge: Schadensersatzdurchsetzung",
    prompt:
      "In welcher Reihenfolge setzt der Patentinhaber seinen Schadensersatzanspruch typischerweise durch?",
    options: [],
    answers: [],
    orderItems: [
      "1. Verletzungsklage erheben und Auskunftsanspruch (§ 140b PatG) geltend machen",
      "2. Auskunft des Verletzers über Umsatz, Gewinn, Abnehmer und Vertriebswege erhalten",
      "3. Berechnungsmethode wählen (konkreter Schaden / Lizenzanalogie / Verletzergewinn)",
      "4. Bezifferten Schadensersatzanspruch nach § 139 Abs. 2 PatG geltend machen",
    ],
    explanation:
      "Der typische Ablauf: Zunächst wird im Rahmen der Verletzungsklage der Auskunftsanspruch nach § 140b PatG geltend gemacht, da ohne Kenntnis der konkreten Umsätze und Gewinne des Verletzers keine Schadensberechnung möglich ist. Nach Erhalt der Auskunft wählt der Verletzte die für ihn günstigste Berechnungsmethode (§ 139 Abs. 2 PatG: entgangener Gewinn, Lizenzanalogie oder Herausgabe des Verletzergewinns) und beziffert dann seinen Anspruch. Die Wahl der Berechnungsmethode ist endgültig.",
    sourceIds: ["patg139", "patg140b"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
      { id: "patg140b", label: "§ 140b PatG", url: "https://www.gesetze-im-internet.de/patg/__140b.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["verletzung", "schadensersatz", "prozess"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-034",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Verletzungsrecht - Instanzenzug",
    title: "Instanzenzug im Patentverletzungsverfahren",
    prompt:
      "Ordnen Sie den Instanzenzug einer deutschen Patentverletzungsklage von der ersten Instanz bis zur Revision.",
    options: [],
    answers: [],
    orderItems: [
      "1. Landgericht (LG) — erste Instanz, § 143 PatG (konzentrierte Zuständigkeit, z.B. LG Düsseldorf)",
      "2. Oberlandesgericht (OLG) — Berufungsinstanz, §§ 511 ff. ZPO",
      "3. Bundesgerichtshof (BGH) — Revisionsinstanz bei zugelassener Revision, §§ 542 ff. ZPO",
    ],
    explanation:
      "§ 143 PatG konzentriert Patentverletzungsklagen bei bestimmten Landgerichten (erste Instanz). Gegen das LG-Urteil ist Berufung zum OLG möglich (§§ 511 ff. ZPO; Berufungsfrist: 1 Monat nach Zustellung, § 517 ZPO). Die Revision zum BGH setzt Revisionszulassung durch das OLG oder eine Divergenzrevision voraus (§§ 542 ff. ZPO). Im Patentrecht sind grundsätzliche Rechtsfragen häufig Revisionsgrund. Der BGH in Karlsruhe ist das zuständige Revisionsgericht.",
    sourceIds: ["patg143"],
    sources: [
      { id: "patg143", label: "§ 143 PatG", url: "https://www.gesetze-im-internet.de/patg/__143.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Schema",
    skillTags: ["verletzung", "prozess", "instanzenzug"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-035",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Verletzungsrecht - Äquivalenz",
    title: "Prüfungsschema: Äquivalenzlehre (BGH 'Schneidmesser')",
    prompt:
      "Prüfen Sie die Äquivalenz eines abweichenden Merkmals in der richtigen Reihenfolge nach der BGH-'Schneidmesser'-Rechtsprechung.",
    options: [],
    answers: [],
    orderItems: [
      "1. Schutzbereichsbestimmung: Patentanspruch wortlautgemäß auslegen (§ 14 PatG)",
      "2. Gleichwirkung: Erzielt das abweichende Mittel dieselbe technische Wirkung wie das Anspruchsmerkmal?",
      "3. Auffindbarkeit: Hätte der Fachmann am Prioritätstag das abweichende Mittel als Austauschmittel aufgefunden?",
      "4. Gleichwertigkeit: Würde der Fachmann das abweichende Mittel als gleichwertige Lösung des der Erfindung zugrundeliegenden Problems ansehen (Orientierung am Anspruch)?",
    ],
    explanation:
      "Die Äquivalenzprüfung nach § 14 PatG beginnt mit der wortwörtlichen Auslegung des Patentanspruchs. Liegt das abweichende Merkmal außerhalb des wörtlichen Schutzbereichs, wird in drei Schritten geprüft: Gleichwirkung → Auffindbarkeit → Gleichwertigkeit (BGH 'Schneidmesser I', GRUR 2002, 511). Alle drei Kriterien müssen kumulativ bejaht werden. Als weiteres Korrektiv ist zu prüfen, ob der Verletzer sich bewusst vom Anspruch entfernt hat (was gegen Äquivalenz sprechen kann).",
    sourceIds: ["patg14"],
    sources: [
      { id: "patg14", label: "§ 14 PatG", url: "https://www.gesetze-im-internet.de/patg/__14.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["verletzung", "aequivalenz", "pruefungsschema"],
    reviewStatus: "beta-ready",
  },

  // ─── CLOZE (5) ──────────────────────────────────────────────────────────────

  {
    id: "da-verletzung-036",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Verletzungsrecht - Schadensersatz",
    title: "Dreifache Schadensberechnung: erste Methode",
    prompt:
      "Nach § 139 Abs. 2 PatG kann der Patentinhaber als erste Berechnungsmethode seinen ___ verlangen.",
    options: [
      "entgangenen Gewinn (konkreter Schaden)",
      "Verletzergewinn in voller Höhe einschließlich Fixkosten",
      "Strafschadensersatz in doppelter Höhe",
      "Umsatz des Verletzers multipliziert mit dem Marktanteil des Patentinhabers",
    ],
    answers: [0],
    explanation:
      "§ 139 Abs. 2 PatG: Die erste Methode ist der konkrete Schaden, insbesondere der entgangene Gewinn des Patentinhabers. Dieser muss konkret darlegen, welche eigenen Verkäufe er wegen der Verletzungshandlung verloren hat. Beim Verletzergewinn (dritte Methode) sind nach BGH 'Faxkarte' (GRUR 2006, 400) und 'Gemeinkostenanteil' (GRUR 2007, 431) nur variable Kosten abzugsfähig — feste Gemeinkosten mindern den herauszugebenden Gewinn nicht.",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["verletzung", "schadensersatz"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-037",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Verletzungsrecht - Verjährung",
    title: "Verjährungsfrist patentrechtlicher Ansprüche",
    prompt:
      "Patentrechtliche Schadensersatzansprüche verjähren nach § 141 PatG i.V.m. § 195 BGB in ___ Jahren.",
    options: [
      "3",
      "5",
      "10",
      "30",
    ],
    answers: [0],
    explanation:
      "§ 141 PatG verweist auf die allgemeinen Verjährungsvorschriften des BGB. Die regelmäßige Verjährungsfrist beträgt nach § 195 BGB 3 Jahre. Sie beginnt nach § 199 Abs. 1 BGB mit Schluss des Jahres, in dem der Anspruch entstanden ist und der Gläubiger Kenntnis von Verletzungshandlung und Verletzer erlangt hat (oder ohne grobe Fahrlässigkeit hätte erlangen müssen). Die absolute Verjährung tritt nach § 199 Abs. 3 BGB in 10 Jahren ein, unabhängig von Kenntnis.",
    sourceIds: ["patg141"],
    sources: [
      { id: "patg141", label: "§ 141 PatG", url: "https://www.gesetze-im-internet.de/patg/__141.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["verletzung", "verjährung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-038",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Verletzungsrecht - Prozess",
    title: "Vollziehungsfrist der einstweiligen Verfügung",
    prompt:
      "Eine einstweilige Verfügung muss nach § 929 Abs. 2 ZPO innerhalb von ___ Monat(en) nach ihrem Erlass vollzogen werden.",
    options: [
      "einem",
      "zwei",
      "drei",
      "sechs",
    ],
    answers: [0],
    explanation:
      "§ 929 Abs. 2 ZPO: Die Vollziehung der einstweiligen Verfügung ist unstatthaft, wenn seit dem Tag, an dem die Verfügung verkündet oder der Partei, auf deren Gesuch sie erging, zugestellt ist, ein Monat verstrichen ist. Die Vollziehung erfolgt durch Zustellung an den Antragsgegner durch Gerichtsvollzieher. Bei Fristversäumnis verliert die Verfügung ihre Wirkung.",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["verletzung", "prozess", "einstweilige-verfuegung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-039",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Verletzungsrecht - Prozess",
    title: "Ordnungsgeld nach § 890 ZPO: Höchstbetrag",
    prompt:
      "Das Ordnungsgeld zur Durchsetzung eines Unterlassungsurteils beträgt nach § 890 ZPO bis zu ___ Euro je Verstoß.",
    options: [
      "250.000",
      "100.000",
      "500.000",
      "50.000",
    ],
    answers: [0],
    explanation:
      "§ 890 Abs. 1 ZPO: Handelt der Schuldner der Verpflichtung zuwider, eine Handlung zu unterlassen, so ist er auf Antrag des Gläubigers durch das Prozessgericht des ersten Rechtszugs wegen einer jeden Zuwiderhandlung zu einem Ordnungsgeld bis zu 250.000 Euro oder zu Ordnungshaft bis zu sechs Monaten zu verurteilen. Das Ordnungsgeld ist kein Strafgeld, sondern Beugemittel. Es erfordert Verschulden des Schuldners und eine vorherige Androhung.",
    sourceIds: ["patg139"],
    sources: [
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["verletzung", "prozess", "vollstreckung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-verletzung-040",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Verletzungsrecht - § 10 PatG",
    title: "Mittelbare Verletzung: subjektives Element",
    prompt:
      "§ 10 Abs. 1 PatG setzt voraus, dass der Lieferant ___ oder es auf der Hand liegt, dass die Mittel zur Benutzung der Erfindung verwendet werden.",
    options: [
      "weiß",
      "fahrlässig nicht weiß",
      "vorsätzlich handelt",
      "wirtschaftlich profitiert",
    ],
    answers: [0],
    explanation:
      "§ 10 Abs. 1 PatG: Die Lieferung von Mitteln ist verboten, wenn der Lieferant weiß oder es auf der Hand liegt (d.h. offensichtlich ist), dass die Mittel zur Benutzung der patentierten Erfindung verwendet werden sollen. 'Auf der Hand liegen' ist objektiv zu verstehen: Bei ausschließlich zur Patentverletzung geeigneten Mitteln kann der Lieferant sich nicht auf Unkenntnis berufen. Einfache Fahrlässigkeit genügt nicht.",
    sourceIds: ["patg10"],
    sources: [
      { id: "patg10", label: "§ 10 PatG", url: "https://www.gesetze-im-internet.de/patg/__10.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["verletzung", "mittelbare-verletzung"],
    reviewStatus: "beta-ready",
  },

  // ─── ZUSATZ-MC: Fallkonstellation (1 weitere, level 3) ─────────────────────

  {
    id: "da-verletzung-041",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Verletzungsrecht - Prozess",
    title: "Fall: Aussetzung nach § 148 ZPO (Trennungsprinzip)",
    prompt:
      "K wird vom Patentinhaber P auf Unterlassung und Schadensersatz verklagt. K reicht beim BPatG Nichtigkeitsklage ein und beantragt beim LG Aussetzung des Verletzungsverfahrens. Das LG hat hierüber zu entscheiden. Welche Aussage ist richtig?",
    options: [
      "Das LG muss das Verfahren zwingend aussetzen, sobald eine Nichtigkeitsklage erhoben wurde.",
      "Das LG hat gemäß § 148 ZPO pflichtgemäßes Ermessen; es setzt aus, wenn die Nichtigkeitsklage hinreichende Erfolgsaussichten hat.",
      "Das LG kann die Nichtigkeit des Patents selbst prüfen und das Verfahren ohne Aussetzung abweisen.",
      "Eine Aussetzung ist unzulässig, weil § 148 ZPO nur für zivilrechtliche Vorfragen, nicht für patentrechtliche Nichtigkeitsfragen gilt.",
    ],
    answers: [1],
    explanation:
      "Trennungsprinzip: Das LG entscheidet über Verletzung, das BPatG über Nichtigkeit (§ 81 PatG). Eine eigene Nichtigkeitsprüfung durch das LG ist nicht möglich. Nach § 148 ZPO kann das Verletzungsgericht nach pflichtgemäßem Ermessen aussetzen, wenn die Entscheidung eines anderen Gerichts vorgreiflich ist — das ist der Fall, wenn die Nichtigkeitsklage hinreichende Aussicht auf Erfolg hat (bei offensichtlich mangelnden Erfolgsaussichten lehnt das LG die Aussetzung ab). Eine automatische Aussetzungspflicht besteht nicht.",
    sourceIds: ["patg143", "patg139"],
    sources: [
      { id: "patg143", label: "§ 143 PatG", url: "https://www.gesetze-im-internet.de/patg/__143.html" },
      { id: "patg139", label: "§ 139 PatG", url: "https://www.gesetze-im-internet.de/patg/__139.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["verletzung", "prozess", "trennungsprinzip", "aussetzung"],
    reviewStatus: "beta-ready",
  },
];
